import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CategoryType } from '.'
import BannerProductbox from '../ads/banner-productbox'
import RecentlyViewed from '@dustin-web/product-components/src/recently-viewed'
import CategoryNavigationItemList from './category-navigation-item-list'
import {
  useLayout,
  useQueryState,
  useResponsive,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import ProductsiteBreadcrumbs from '../utils/productsite-breadcrumbs'
import { ProductListOptions } from '@dustin-web/product-components/src/product-list'
import { ProductInfinityList } from '@dustin-web/product-components/src/product-list/product-infinity-list'
import { BOOLEAN_FACET_NAME, FilterBox, Filters } from '@dustin-web/product-components/src/filters'
import { ActiveFilters } from '@dustin-web/product-components/src/filters/active-filters'
import { PopoverInitializer } from '../utils'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import Link from 'next/link'
import { getCategoryUrl } from '@dustin-web/microsite-components/src/utils/urls'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import { ScrollToResults } from './scroll-to-results'
import { GetCategoryForFilterPageQuery } from '@productsite/src/graphql/generated/graphql-types'
import { flatten, isNotNullOrUndefined } from '@dustin-web/microsite-data/src/utils'
import Button from '@dustin-web/microsite-components/src/button'
import classNames from 'classnames'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import Collapse from '@dustin-web/microsite-components/src/collapse'
import { FilterPageType, type FilterPageMetaData } from './filter-page-layout'
import PlpBrandBanner, { usePlpBannerVisible } from '../ads/plp-brand-banner'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'
import { Icon } from '@dustin-web/microsite-components/src/icon-library'

const legacyBooleanFacetNames = ['instock', 'campaign', 'isnewproduct']

type Props = {
  paginatedResults: NonNullable<GetCategoryForFilterPageQuery[]>
  category: CategoryType
  loading: boolean
  pageMetaData: FilterPageMetaData
  page: number
  handleLoadMore: () => void
}

const FilterPage = ({
  paginatedResults,
  category,
  loading,
  pageMetaData,
  page,
  handleLoadMore,
}: Props) => {
  const { queryState, setQueryState } = useQueryState()
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [pageInitialized, setPageInitialized] = useState(false)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const { responsive } = useLayout()
  const { translate } = useTranslations()
  const { breakpoints } = useResponsive()
  const { query } = useCleanRouter()
  const filterRef = useRef<HTMLDivElement | null>(null)
  const productListRef = useRef<HTMLDivElement | null>(null)

  const productsToShow = paginatedResults
    .map(d =>
      d.category?.productSearch?.productReferences.filter(
        list => list?.searchResultProduct?.availability?.isAvailableForSale
      )
    )
    .filter(isNotNullOrUndefined)

  const { activeFilters, searchwithin } = queryState
  const { enabled: isCampaignBannerEnabled } = useFeatureFlag('show-plp-campaign-banner') ?? {}
  const bannerVisible = usePlpBannerVisible(activeFilters, category.pathSlug)
  const showBanner = isCampaignBannerEnabled && bannerVisible

  useEffect(() => {
    // don't count 'path'
    const filters = queryState.activeFilters.filter(f => f.name !== 'path')
    setHasActiveFilters(filters.length > 0)
  }, [queryState])

  useEffect(() => {
    PopoverInitializer.init()

    if (!pageInitialized) {
      if (category.navigation && category.productSearch) {
        ProductDigitalData._init({
          navigation: category.navigation,
        })

        if (
          !searchwithin &&
          activeFilters.length === 0 &&
          category.productSearch.productReferences.map(p => p.searchResultProduct).filter(Boolean)
            .length === 0
        ) {
          // eslint-disable-next-line no-console
          console.error(
            `No matching products for category: ${category.pathSlug}. HasVisibleProducts / IsLeaf is likely incorrect. Check for hidden products.`
          )
        }
      }

      setPageInitialized(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Remove faulty filters
    const faultyFilters = queryState.activeFilters.filter(activeFilter => {
      if ([BOOLEAN_FACET_NAME, ...legacyBooleanFacetNames].includes(activeFilter.name)) {
        return false
      }

      const facet =
        category?.productSearch?.facets.find((f: any) => f.nameInUrl === activeFilter.name) ??
        category?.productSearch?.filterOnlyFacets?.find(
          (f: any) => f.nameInUrl === activeFilter.name
        )

      if (!facet) {
        return true
      }

      if (facet.type !== 'MULTIPLE') {
        return false
      }

      const faultyValues = activeFilter.values.filter(activeFilterValue =>
        facet.values.find(facetValue => {
          if (activeFilter.name !== facet.nameInUrl) {
            return false
          }

          return activeFilterValue === facetValue.name && facetValue.hits === 0
        })
      )

      if (faultyValues.length === activeFilter.values.length) {
        return true
      }
    })

    if (faultyFilters.length > 0) {
      const keys = faultyFilters.reduce(
        (acc, filter) => {
          acc[filter.name] = ''
          return acc
        },
        {} as { [key: string]: string }
      )

      // eslint-disable-next-line no-console
      console.warn(
        'Selected filters in URL do not match available filters, removing them.',
        faultyFilters
      )
      setQueryState(keys)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(category.productSearch?.facets)])

  const clearFilters = useCallback(() => {
    const keys = activeFilters.reduce(
      (acc, activeFilter) => {
        acc[activeFilter.name] = ''
        return acc
      },
      {} as { [key: string]: string }
    )

    keys[BOOLEAN_FACET_NAME] = ''
    keys.page = ''

    setQueryState(keys)
  }, [activeFilters, setQueryState])

  const cleanedSeoText = useMemo(
    () =>
      category.seoText
        ?.replace(/<h1>.+<\/h1>/g, '') // Remove h1 tags
        .replace(/<h2>/g, '<h2 class="h3">') // Add className to h2 tags
        .replace(/<h3>/g, '<h2 class="h4">'), // Add className to h3 tags
    [category.seoText]
  )

  const products = useMemo(() => flatten(productsToShow), [productsToShow])

  if (!category.navigation?.path || !category.productSearch) {
    return null
  }

  const navigation = category.navigation
  const path = navigation.path
  const productSearch = category.productSearch
  const parentCategoryNavItem = path.length >= 2 ? path[path.length - 2] : null
  const parentCategory = parentCategoryNavItem?.category
  const parentCategoryUrl = getCategoryUrl(parentCategory?.pathSlug)
  const isSoftCategory = category.id?.includes('StructureGroup_')

  query?.delete('page')
  const queryWithQuestionMark = query?.toString() ? `?${query}` : ''

  const categoryHasChildren = navigation.childrenSortedByHits.length > 0
  const parentIndexInPath = categoryHasChildren ? path.length - 2 : path.length - 3
  const superParentCategory = path[parentIndexInPath]

  const { type, facetsFilterExpression, title, seoText, digitalDataOrigin, showOnlyUrlQuery } =
    pageMetaData

  const facets =
    type !== FilterPageType.Category
      ? productSearch.facets.filter(item => {
          return item.name !== facetsFilterExpression
        })
      : productSearch.facets

  const urlToSuperParentCategory = `${getCategoryUrl(
    superParentCategory?.category?.pathSlug
  )}${queryWithQuestionMark}`

  // black friday placeholder id, may change
  const blackFridayCategoryId = 'StructureGroup_1591819568058008'
  // check if blackfriday category id is part of the categorypath
  const isBlackFridayParentCategory = category?.navigation?.path.some(
    category => category.categoryId === blackFridayCategoryId
  )

  return (
    <>
      <div
        id="page-container"
        className={classNames('c-filter', {
          't-campaign-page': isBlackFridayParentCategory,
        })}
        data-gasource="CategoryListingPage">
        <div className="container">
          <ProductsiteBreadcrumbs path={path} pageTitle={title} />
        </div>
        <div className="container">
          <div className="row">
            <div className={showBanner ? 'col-12 col-lg-7' : 'col-12'}>
              {category.seoText ? (
                <>
                  <h1
                    className={classNames('h2', {
                      'u-mb-24': !cleanedSeoText || cleanedSeoText.length === 0,
                    })}>
                    {category.seoLongTitleName || category.name}
                  </h1>
                  <Collapse heightLimit={60} colorTheme="gray" text={cleanedSeoText ?? ''} />
                </>
              ) : parentCategory ? (
                <h1 className="h2 u-mb-16 u-pt-16 u-pt-lg-0 u-pl-16 u-pl-lg-0">
                  {category.seoLongTitleName || category.name}
                </h1>
              ) : type !== FilterPageType.Category ? (
                <>
                  <h1
                    className={classNames('h2', {
                      'u-mb-24': !title,
                    })}>
                    {title}
                  </h1>
                  <Collapse heightLimit={60} colorTheme="gray" text={seoText} />
                </>
              ) : (
                <h1 className="h4 u-mb-16 u-pt-16 u-pt-lg-0 u-pl-16 u-pl-lg-0">
                  {translate('Products')}
                </h1>
              )}
            </div>
            {showBanner && (
              <div className="col-12 col-lg-5 u-pb-16 u-pb-lg-0">
                <PlpBrandBanner activeFilters={activeFilters} pathSlug={category.pathSlug} />
              </div>
            )}
          </div>
        </div>
        <div className="container u-mb-24">
          <div className="row sm-gutters">
            <Drawer
              visible={showMobileFilter && responsive.isSmallScreen}
              onClose={() => setShowMobileFilter(false)}
              returnOnlyChildren={!responsive.isSmallScreen}
              className="row no-gutters u-px-0 u-py-0">
              <div className="col-12 col-lg-3">
                <FilterBox
                  noIcon
                  title={translate('CategoryHeadline')}
                  titleClassName={responsive.isSmallScreen ? 'u-fs-16' : ''}
                  withPadding>
                  <>
                    {superParentCategory && !isSoftCategory && (
                      <div className="u-pb-12">
                        <Link
                          onClick={() => {
                            superParentCategory.category?.englishCategoryName &&
                              DigitalData.pushFilterCatClick(
                                superParentCategory.category?.englishCategoryName
                              )
                          }}
                          prefetch={false}
                          href={urlToSuperParentCategory}
                          className="a-stripped d-flex align-items-center">
                          <Icon
                            name="arrow-left-small"
                            className="i-24"
                            style={{ marginLeft: '-8px' }}
                          />
                          <span className="u-pt-2">
                            {superParentCategory?.category?.seoLongTitleName ??
                              superParentCategory?.category?.name}
                          </span>
                        </Link>
                      </div>
                    )}
                    {categoryHasChildren ? (
                      <>
                        <h3 className="h4 u-fs-14 u-fw-bold">
                          {category.seoLongTitleName || category.name}
                        </h3>
                        <CategoryNavigationItemList
                          showAll={false}
                          navigationItems={navigation.childrenSortedByHits}
                          showOnlyUrlQuery={showOnlyUrlQuery}
                          pageType={type}
                          onItemClick={val => DigitalData.pushFilterCatClick(val)}
                        />
                      </>
                    ) : (
                      <>
                        {parentCategoryUrl && (
                          <h3 className="h4 u-fs-14 u-fw-normal">
                            <Link
                              onClick={() => {
                                parentCategory?.englishCategoryName &&
                                  DigitalData.pushFilterCatClick(
                                    parentCategory?.englishCategoryName
                                  )
                              }}
                              prefetch={false}
                              href={`${parentCategoryUrl}${queryWithQuestionMark}`}>
                              {parentCategory?.seoLongTitleName ?? parentCategory?.name ?? ''}
                            </Link>
                          </h3>
                        )}

                        {navigation.siblings.length > 0 && (
                          <CategoryNavigationItemList
                            showAll={false}
                            navigationItems={navigation.siblings}
                            pageType={type}
                            onItemClick={val => DigitalData.pushFilterCatClick(val)}
                          />
                        )}
                      </>
                    )}
                  </>
                </FilterBox>
                {facets.length > 0 && (
                  <>
                    <FilterBox
                      sticky={!responsive.isSmallScreen}
                      stickyOffset={120}
                      ref={filterRef}
                      title={translate('mobilefilterlabel')}
                      className={responsive.isSmallScreen ? 'u-bt u-border-light' : ''}
                      titleClassName={responsive.isSmallScreen ? 'u-fs-16' : ''}
                      headerClassName={responsive.isSmallScreen && hasActiveFilters ? 'u-pb-8' : ''}
                      additionalTopContent={
                        <Button
                          defaultBtn
                          className={classNames('button-stripped p-sm u-mb-0', {
                            'u-decoration-none': !hasActiveFilters,
                          })}
                          text={translate('ResetAllFilters')}
                          onClick={clearFilters}
                          disabled={!hasActiveFilters}></Button>
                      }
                      activeFilters={
                        showMobileFilter ? (
                          <ActiveFilters facetGroups={facets || []} mobile />
                        ) : null
                      }>
                      <Filters facets={facets} />
                    </FilterBox>
                    {responsive.isSmallScreen && (
                      <div
                        className="u-sticky-bottom u-bg-white u-px-16 u-py-16 d-flex filter-reset"
                        style={{ zIndex: 10 }}>
                        <Button
                          className="c-btn u-mb-0 u-w-shrink"
                          text={translate('ResetAllFilters')}
                          disabled={!hasActiveFilters}
                          onClick={() => clearFilters()}></Button>
                        <Button
                          className="c-btn c-btn--primary u-flex-1 u-mb-0"
                          text={translate(
                            hasActiveFilters
                              ? 'MobileCloseAndShowProductsFilterLabel'
                              : 'MobileCloseLabel',
                            { count: productSearch.productHits }
                          )}
                          onClick={() => setShowMobileFilter(false)}></Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Drawer>
            <div id="filter-product-list" className="col-12 col-lg-9">
              {productSearch.ads.length > 0 && <BannerProductbox ad={productSearch.ads[0]} />}
              <ProductListOptions
                disablePageSize
                listCategoryName={category.seoLongTitleName || category.name}
                listCategoryId={category.id}
                onMobileFilterOverlayOpen={() => setShowMobileFilter(true)}
              />
              <ActiveFilters
                facetGroups={[...facets, ...(productSearch.filterOnlyFacets || [])]}
                onClearFilter={() => {
                  clearFilters()
                }}
              />
              <div ref={productListRef} className="u-mb-16">
                <ProductInfinityList
                  showSideButtons={breakpoints.max.SM}
                  handleLoadMore={handleLoadMore}
                  totalHits={productSearch.productHits}
                  productReferences={productsToShow}
                  isLoadingMore={loading}
                  searchPhrase={searchwithin}
                  digitalDataOrigin={digitalDataOrigin}
                  page={page}
                  hTag={'h2'}
                  categoryId={category?.id}
                />
              </div>
              <ScrollToResults
                productListForwardRef={productListRef as React.RefObject<HTMLElement>}
                filterForwardRef={filterRef as React.RefObject<HTMLElement>}
                totalResults={products.length}
                idToScrollTo="filter-product-list"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={'d-none d-sm-block'}>
        <RecentlyViewed />
      </div>
    </>
  )
}

export default FilterPage
